export type IftaStateLine = {
  state: string;
  tax: number;
  taxableMiles: number;
  totalMiles: number;
  fuelAmount: number;
  taxableGallons: number;
  taxPaidGallons: number;
  netTaxableGallons: number;
  overcharge: boolean;
};

export type IftaFilingLine = {
  truckNumber: string;
  milesPerGallon: number;
  stateLines: IftaStateLine[];
  totalTax: number;
  totalMiles: number;
  totalTaxableMiles: number;
  totalFuelAmount: number;
  totalTaxableGallons: number;
  totalTaxPaidGallons: number;
  totalNetTaxableGallons: number;
};

export type IftaFuelLine = {
  importedFromSystem: string;
  importedFromFile: string;
  truckNumber: string;
  state: string;
  gallons: number;
  amount: number;
};

export type IftaMileLine = {
  importedFromSystem: string;
  importedFromFile: string;
  truckNumber: string;
  state: string;
  taxableMiles: number;
  totalMiles: number;
};

export type IftaUploadedFile = {
  fileName: string;
  fileUrl: string;
  fileId: string;
  uploadedAt: string | null;
  uploadedBy: string;
  source: string;
};

export const SOURCE_LABELS: Record<string, string> = {
  samsara: 'Samsara',
  motive: 'Motive',
  counting_trucks: 'Counting Trucks',
  // ai_eld: 'AI ELD',
  // sparkle: 'Sparkle',
  other: 'Other provider',
  efs: 'EFS',
  tch: 'TCH',
  comdata: 'Comdata',
  fleet_one: 'Fleet One',
  fuel_card: 'Fuel card',
  manual: 'Manual entry',
};

export type PaymentStatus = 'unpaid' | 'pending' | 'paid';

export type MilesSource =
  'samsara' | 'motive' | 'counting_trucks' | 'ai_eld' | 'sparkle' | 'other' | '';

export type IftaFiling = {
  id: number;
  createdAt: string;
  title: string;
  fuelLines: IftaFuelLine[];
  mileLines: IftaMileLine[];
  filingLines: IftaFilingLine[];
  totalFilingLine: IftaFilingLine;
  fuelFiles: IftaUploadedFile[];
  mileFiles: IftaUploadedFile[];
  byTruck: boolean;
  roundPerTruckState: boolean;
  selectedQuarter: string;
  selectedState: string;
  acknowledgedIssues: string[];
  milesSource: MilesSource;
  paymentStatus: PaymentStatus;
  priceCents: number;
  truckCount: number;
  paidAt: string | null;
  filedAt: string | null;
};

export type ColumnMapping = {
  vehicleColumn: number;
  stateColumn: number;
  totalMilesColumn: number;
  taxableMilesColumn: number;
  gallonsColumn: number;
  amountColumn: number;
  filterColumn: number;
  filterValue: string;
};

export type UploadResponse = {
  filing: IftaFiling;
  mapping: ColumnMapping;
  mappingSource: 'builtin' | 'cached' | 'ai';
  headers: string[];
  unknownJurisdictions: string[];
};

export const FIRST_FILING_DISCOUNT_PERCENT = 10;

// Tiered list price per truck by fleet size; must match the backend
// (backend/app/ifta_engine/pricing.go).
export function unitPriceCents(truckCount: number): number {
  if (truckCount <= 1) return 4999;
  if (truckCount <= 10) return 1999;
  if (truckCount <= 100) return 499;
  if (truckCount <= 200) return 149;
  return 49;
}

export const JURISDICTIONS = [
  'AB',
  'BC',
  'MB',
  'NB',
  'NL',
  'NS',
  'ON',
  'PE',
  'QC',
  'SK',
  'AL',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];

export const FUEL_PROVIDERS: { value: string; label: string }[] = [
  { value: 'efs', label: 'EFS' },
  { value: 'tch', label: 'TCH' },
  { value: 'comdata', label: 'Comdata' },
  { value: 'fleet_one', label: 'Fleet One' },
  { value: 'other', label: 'Other provider' },
];

export const PAYMENT_STATUS_LABELS: Record<PaymentStatus, string> = {
  unpaid: 'Not Done',
  pending: 'Payment Pending',
  paid: 'Calculated',
};

export const MILES_SOURCES: { value: MilesSource; label: string }[] = [
  { value: 'samsara', label: 'Samsara' },
  { value: 'motive', label: 'Motive' },
  { value: 'counting_trucks', label: 'Counting Trucks' },
  // { value: 'ai_eld', label: 'AI ELD' },
  // { value: 'sparkle', label: 'Sparkle' },
  { value: 'other', label: 'Other provider' },
];
